import {
  isPmsReportId,
  PMS_REPORT_FILTER_KEYS,
  PMS_REPORT_FILTER_VALUES,
  PMS_REPORT_GROUPS,
  PMS_REPORTS,
  type PmsReportCellKind,
  type PmsReportDef,
  type PmsReportFormat,
  type PmsReportGroup,
  type PmsReportId,
  pmsReportDef,
} from '@felix/domain';
import { Alert, Button, Select } from '@mantine/core';
import {
  IconArrowLeft,
  IconCalendarEvent,
  IconCalendarMonth,
  IconCalendarWeek,
  IconCertificate,
  IconFileSpreadsheet,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconInfoCircle,
  IconShieldCheck,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { sortItems } from '../purchasing/requisitions.js';
import { SortableTh, type SortState, TablePager } from '../purchasing/table.js';
import classes from './Pms.module.css';
import css from './PmsReports.module.css';
import { LoadFailed, RowsSkeleton } from './PmsWorkParts.js';
import {
  type PmsReportView,
  pmsReportDocument,
  type ReportDocumentCell,
  type ReportDocumentTable,
  type ReportT,
} from './report-document.js';
import { savePmsReportPdf } from './report-print.js';
import type { PmsReportFilter, PmsSource } from './source.js';

/** Adres sorgusunda raporun süzgeç anahtarları (Reports sekmesi). */
export const REPORT_QUERY_KEYS = [
  'report',
  'from',
  'to',
  'year',
  ...PMS_REPORT_FILTER_KEYS,
] as const;

const GROUP_ICONS: Record<PmsReportGroup, ReactNode> = {
  daily: <IconCalendarEvent size={20} />,
  weekly: <IconCalendarWeek size={20} />,
  monthly: <IconCalendarMonth size={20} />,
  class: <IconCertificate size={20} />,
  nr496: <IconShieldCheck size={20} />,
};

const PAGE_SIZE = 15;

/** Adresteki süzgeç (`?report=…&from=…&critical=yes`): yalnız raporun dönemine ve süzgeçlerine uyanlar. */
export function reportFilterOf(def: PmsReportDef, query: URLSearchParams): PmsReportFilter {
  const filter: PmsReportFilter = {};
  const day = (value: string | null) =>
    value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
  if (def.period === 'range' || def.period === 'day') {
    const from = day(query.get('from'));
    const to = day(query.get('to'));
    if (def.period === 'range' && from) filter.from = from;
    if (to) filter.to = to;
  }
  if (def.period === 'year') {
    const year = Number(query.get('year'));
    if (Number.isInteger(year) && year >= 2000 && year <= 2100) filter.year = year;
  }
  for (const key of def.filters ?? []) {
    const value = query.get(key);
    if (value && PMS_REPORT_FILTER_VALUES[key].includes(value)) filter[key] = value;
  }
  return filter;
}

/** Ekranda hücre: gün ekran biçimiyle, an UTC, sayı dile göre (PDF'teki biçim). */
export function reportCellText(
  kind: PmsReportCellKind,
  value: ReportDocumentCell,
  locale: string,
): string {
  if (value === null || value === '') return '—';
  if (kind === 'date' && typeof value === 'string') return formatDay(value);
  if (kind === 'datetime' && typeof value === 'string') return formatDateTime(value, 0);
  if (typeof value === 'number') {
    return value.toLocaleString(locale, { maximumFractionDigits: kind === 'hours' ? 1 : 3 });
  }
  return String(value);
}

const numeric = (kind: PmsReportCellKind) =>
  kind === 'number' || kind === 'hours' || kind === 'percent';

type FileKind = 'pdf' | PmsReportFormat;

/** PDF (istemcide), Excel ve CSV (sunucuda): hangi rapor ve biçim hazırlanıyor, hata. */
function useReportFiles(source: PmsSource, vesselId: string | null) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<FileKind | null>(null);
  const run = useCallback(
    async (
      reportId: PmsReportId,
      kind: FileKind,
      query: PmsReportFilter,
      loaded?: PmsReportView | null,
    ) => {
      if (!vesselId) return;
      setBusy(`${reportId}:${kind}`);
      setFailed(null);
      const lang = i18n.resolvedLanguage?.startsWith('tr') ? 'tr' : 'en';
      try {
        if (kind === 'pdf') {
          if (!source.report) throw new Error('UNAVAILABLE');
          const data = loaded ?? (await source.report(vesselId, reportId, query));
          await savePmsReportPdf(source, data, t as unknown as ReportT, lang);
        } else {
          if (!source.exportReport) throw new Error('UNAVAILABLE');
          await source.exportReport(vesselId, reportId, { format: kind, lang, query });
        }
      } catch {
        setFailed(kind);
      } finally {
        setBusy(null);
      }
    },
    [source, vesselId, t, i18n.resolvedLanguage],
  );
  return { busy, failed, dismiss: () => setFailed(null), run };
}

function FileButtons({
  reportId,
  title,
  busy,
  disabled,
  onRun,
  size = 'xs',
}: {
  reportId: PmsReportId;
  title: string;
  busy: string | null;
  disabled: boolean;
  onRun: (kind: FileKind) => void;
  size?: 'compact-xs' | 'xs' | 'sm';
}) {
  const { t } = useTranslation();
  const u = (key: string) => t(`pmsReports.ui.${key}`, { name: title });
  const items: [FileKind, ReactNode, string, string][] = [
    ['pdf', <IconFileTypePdf key="pdf" size={14} />, t('pmsReports.export.pdf'), u('pdfOf')],
    [
      'xlsx',
      <IconFileSpreadsheet key="xlsx" size={14} />,
      t('pmsReports.export.excel'),
      u('excelOf'),
    ],
    ['csv', <IconFileTypeCsv key="csv" size={14} />, t('pmsReports.export.csv'), u('csvOf')],
  ];
  return (
    <>
      {items.map(([kind, icon, label, aria]) => (
        <Button
          key={kind}
          size={size}
          variant="default"
          leftSection={icon}
          loading={busy === `${reportId}:${kind}`}
          disabled={disabled || (busy !== null && busy !== `${reportId}:${kind}`)}
          aria-label={aria}
          onClick={() => onRun(kind)}
        >
          {label}
        </Button>
      ))}
    </>
  );
}

export interface ReportsTabProps {
  source: PmsSource;
  vesselId: string | null;
  query: URLSearchParams;
  update: (patch: Record<string, string | null>) => void;
}

/**
 * History ▸ Reports (plan M7 Task 31): rapor kartları beş grupta (Daily, Weekly, Monthly, Class,
 * NR496); her kartta önizleme, "Download PDF", "Excel", "CSV". Önizleme raporun dönemini ve
 * süzgeçlerini adreste tutar; tablolar sıralanır ve sayfalanır. Raporlar yalnız görüntülenir ve
 * dışa aktarılır [NR496 4.6]; PDF istemcide üretilir (gemide internetsiz).
 */
export function ReportsTab({ source, vesselId, query, update }: ReportsTabProps) {
  const reportId = query.get('report');
  const files = useReportFiles(source, vesselId);
  if (reportId && isPmsReportId(reportId)) {
    const def = pmsReportDef(reportId);
    return (
      <ReportPreview
        key={reportId}
        source={source}
        vesselId={vesselId}
        def={def}
        filter={reportFilterOf(def, query)}
        files={files}
        onChange={(patch) => update(patch)}
        onBack={() => update(Object.fromEntries(REPORT_QUERY_KEYS.map((key) => [key, null])))}
      />
    );
  }
  return (
    <ReportCatalog
      files={files}
      disabled={!vesselId}
      onPreview={(id) => update({ report: id })}
      onRun={(id, kind) => void files.run(id, kind, {})}
    />
  );
}

type Files = ReturnType<typeof useReportFiles>;

/** Kart ızgarası: grup başına bir panel. */
export function ReportCatalog({
  files,
  disabled,
  onPreview,
  onRun,
}: {
  files: Pick<Files, 'busy' | 'failed' | 'dismiss'>;
  disabled: boolean;
  onPreview: (id: PmsReportId) => void;
  onRun: (id: PmsReportId, kind: FileKind) => void;
}) {
  const { t } = useTranslation();
  const u = (key: string, values: Record<string, unknown> = {}) =>
    t(`pmsReports.ui.${key}`, values);
  return (
    <div className={css.catalog}>
      <p className={css.intro}>{u('description')}</p>
      <FilesFailed failed={files.failed} onClose={files.dismiss} />
      {PMS_REPORT_GROUPS.map((group) => {
        const groupName = t(`pmsReports.groups.${group}`);
        return (
          <Panel key={group} title={u('group', { group: groupName })}>
            <ul className={css.grid} aria-label={u('group', { group: groupName })}>
              {PMS_REPORTS.filter((def) => def.group === group).map((def) => {
                const title = t(`pmsReports.reports.${def.id}.title`);
                return (
                  <li key={def.id} className={css.card} data-report={def.id}>
                    <span className={css.icon} aria-hidden>
                      {GROUP_ICONS[group]}
                    </span>
                    <h3 className={css.title}>{title}</h3>
                    <p className={css.text}>
                      {t(`pmsReports.reports.${def.id}.description`)}
                      <span className={css.rule}>{def.rule}</span>
                    </p>
                    <div className={css.actions}>
                      <Button
                        size="compact-xs"
                        variant="light"
                        disabled={disabled}
                        aria-label={u('previewOf', { name: title })}
                        onClick={() => onPreview(def.id)}
                      >
                        {u('preview')}
                      </Button>
                      <FileButtons
                        size="compact-xs"
                        reportId={def.id}
                        title={title}
                        busy={files.busy}
                        disabled={disabled}
                        onRun={(kind) => onRun(def.id, kind)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Panel>
        );
      })}
    </div>
  );
}

function FilesFailed({ failed, onClose }: { failed: FileKind | null; onClose: () => void }) {
  const { t } = useTranslation();
  if (!failed) return null;
  return (
    <Alert
      color="red"
      variant="light"
      withCloseButton
      closeButtonLabel={t('pms.work.detail.dismiss')}
      onClose={onClose}
      role="alert"
    >
      {failed === 'pdf' ? t('pmsReports.ui.pdfFailed') : t('pmsReports.export.exportFailed')}
    </Alert>
  );
}

export type ReportState =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: PmsReportView };

function ReportPreview({
  source,
  vesselId,
  def,
  filter,
  files,
  onChange,
  onBack,
}: {
  source: PmsSource;
  vesselId: string | null;
  def: PmsReportDef;
  filter: PmsReportFilter;
  files: Files;
  onChange: (patch: Record<string, string | null>) => void;
  onBack: () => void;
}) {
  const [state, setState] = useState<ReportState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify(filter);
  // biome-ignore lint/correctness/useExhaustiveDependencies: süzgeç `key` ile, yeniden dene `attempt` ile
  useEffect(() => {
    if (!vesselId || !source.report) return;
    let live = true;
    setState({ kind: 'loading' });
    source
      .report(vesselId, def.id, filter)
      .then((data) => {
        if (live) setState({ kind: 'ready', data });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, def.id, key, attempt]);
  return (
    <ReportPreviewView
      def={def}
      filter={filter}
      state={state}
      busy={files.busy}
      failed={files.failed}
      onDismiss={files.dismiss}
      onRun={(kind) =>
        void files.run(def.id, kind, filter, state.kind === 'ready' ? state.data : null)
      }
      onChange={onChange}
      onBack={onBack}
      onRetry={() => setAttempt((n) => n + 1)}
    />
  );
}

export interface ReportPreviewViewProps {
  def: PmsReportDef;
  filter: PmsReportFilter;
  state: ReportState;
  busy: string | null;
  failed: FileKind | null;
  onDismiss: () => void;
  onRun: (kind: FileKind) => void;
  onChange: (patch: Record<string, string | null>) => void;
  onBack: () => void;
  onRetry: () => void;
}

/** Raporun önizlemesi: dönem ve süzgeçler, kaynak ve dayanak, tablolar, özet. */
export function ReportPreviewView({
  def,
  filter,
  state,
  busy,
  failed,
  onDismiss,
  onRun,
  onChange,
  onBack,
  onRetry,
}: ReportPreviewViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const u = (key: string, values: Record<string, unknown> = {}) =>
    t(`pmsReports.ui.${key}`, values);
  const title = t(`pmsReports.reports.${def.id}.title`);
  const data = state.kind === 'ready' ? state.data : null;
  const doc = useMemo(
    () =>
      data
        ? pmsReportDocument(
            data,
            {
              version: '',
              printedAt: data.generatedAt,
              printedBy: { name: '', role: null },
              tzOffsetMin: 0,
              brand: null,
              locale,
            },
            t as unknown as ReportT,
          )
        : null,
    [data, locale, t],
  );
  const years = useMemo(() => {
    const current = Number((data?.asOf ?? new Date().toISOString()).slice(0, 4));
    return Array.from({ length: 6 }, (_, i) => String(current - i));
  }, [data?.asOf]);
  const choice = (key: (typeof PMS_REPORT_FILTER_KEYS)[number]) => (
    <Select
      key={key}
      label={t(`pmsReports.filters.${key}`)}
      data={[
        { value: '', label: t('pmsReports.filters.any') },
        ...PMS_REPORT_FILTER_VALUES[key].map((value) => ({
          value,
          label: t(`pmsReports.filters.${value}`),
        })),
      ]}
      value={filter[key] ?? ''}
      allowDeselect={false}
      onChange={(value) => onChange({ [key]: value || null })}
    />
  );
  let body: ReactNode;
  if (state.kind === 'error') {
    body = <LoadFailed code={state.code} onRetry={onRetry} />;
  } else if (!doc || !data) {
    body = (
      <section aria-busy="true" aria-label={u('loading')}>
        <RowsSkeleton rows={8} />
      </section>
    );
  } else {
    body = (
      <>
        <dl className={css.meta}>
          <div>
            <dt>{t('pmsReports.export.period')}</dt>
            <dd>
              {data.from && data.to
                ? data.from === data.to
                  ? formatDay(data.from)
                  : u('period', { from: formatDay(data.from), to: formatDay(data.to) })
                : u('asOf', { date: formatDay(data.asOf) })}
            </dd>
          </div>
          <div>
            <dt>{u('source')}</dt>
            <dd>
              {doc.basis} ({doc.sources})
            </dd>
          </div>
          <div>
            <dt>{u('rule')}</dt>
            <dd>{doc.rule}</dd>
          </div>
          <div>
            <dt>{u('hash')}</dt>
            <dd className={css.hash}>{data.hash}</dd>
          </div>
        </dl>
        {doc.notes.map((note) => (
          <Alert
            key={note}
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
            mb="sm"
          >
            {note}
          </Alert>
        ))}
        {doc.tables.map((table) => (
          <ReportTable
            key={table.key}
            table={table}
            locale={locale}
            single={doc.tables.length === 1}
          />
        ))}
        {doc.summary.length > 0 ? (
          <dl className={css.summary} aria-label={t('pmsReports.export.summary')}>
            {doc.summary.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </>
    );
  }
  return (
    <Panel title={title}>
      <div className={css.previewHead}>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<IconArrowLeft size={14} />}
          onClick={onBack}
        >
          {u('back')}
        </Button>
        <span className={css.headActions}>
          <FileButtons
            reportId={def.id}
            title={title}
            busy={busy}
            disabled={state.kind !== 'ready'}
            onRun={onRun}
          />
        </span>
      </div>
      <p className={css.intro}>{t(`pmsReports.reports.${def.id}.description`)}</p>
      <FilesFailed failed={failed} onClose={onDismiss} />
      {def.period !== 'none' || (def.filters ?? []).length > 0 ? (
        <form
          className={css.filters}
          aria-label={t('pmsReports.filters.title')}
          onSubmit={(event) => event.preventDefault()}
        >
          {def.period === 'range' ? (
            <>
              <DayInput
                label={u('from')}
                value={data?.from ?? filter.from ?? null}
                onChange={(value) => onChange({ from: value })}
              />
              <DayInput
                label={u('to')}
                value={data?.to ?? filter.to ?? null}
                onChange={(value) => onChange({ to: value })}
              />
            </>
          ) : null}
          {def.period === 'day' ? (
            <DayInput
              label={u('day')}
              value={data?.to ?? filter.to ?? null}
              onChange={(value) => onChange({ to: value })}
            />
          ) : null}
          {def.period === 'year' ? (
            <Select
              label={u('year')}
              data={years}
              value={String(filter.year ?? data?.from?.slice(0, 4) ?? years[0])}
              allowDeselect={false}
              onChange={(value) => onChange({ year: value })}
            />
          ) : null}
          {(def.filters ?? []).map((key) => choice(key))}
        </form>
      ) : null}
      {body}
    </Panel>
  );
}

/** Raporun bir tablosu: işaret sütunu, sıralanır ve sayfalanır. */
export function ReportTable({
  table,
  locale,
  single,
}: {
  table: ReportDocumentTable;
  locale: string;
  single: boolean;
}) {
  const { t } = useTranslation();
  const u = (key: string, values: Record<string, unknown> = {}) =>
    t(`pmsReports.ui.${key}`, values);
  const [sort, setSort] = useState<SortState<string> | null>(null);
  const [page, setPage] = useState(1);
  const indexed = useMemo(
    () =>
      table.rows.map((row, i) => ({
        at: i,
        row,
        mark: table.marks[i] ?? null,
        label: table.markLabels[i] ?? null,
      })),
    [table],
  );
  const rows = useMemo(() => {
    if (!sort) return indexed;
    const at = table.columns.findIndex((column) => column.key === sort.key);
    return sortItems(indexed, sort.dir, (item) => item.row[at] ?? null);
  }, [indexed, sort, table.columns]);
  const withMark = table.marks.some((mark) => mark !== null);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  return (
    <div>
      {single ? null : <h3 className={css.tableTitle}>{table.title}</h3>}
      {rows.length === 0 ? (
        <EmptyState title={u('empty')} />
      ) : (
        <>
          {/* Rapor önizlemesi dışa aktarılan raporun bütün sütunlarını gösterir; bilerek yana
              kayar, sütun gizlenmez (e2e tablo kayma denetimi) */}
          <section
            className={classes.tableBox}
            data-allow-scroll
            aria-label={u('tableOf', { name: table.title })}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table} data-cards>
              <thead>
                <tr>
                  {withMark ? <th scope="col">{u('mark')}</th> : null}
                  {table.columns.map((column) => (
                    <SortableTh
                      key={column.key}
                      column={column.key}
                      label={column.label}
                      sort={sort ?? { key: '', dir: 'asc' }}
                      numeric={numeric(column.kind)}
                      onSort={(next) => {
                        setSort(next);
                        setPage(1);
                      }}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr key={item.at} data-mark={item.mark ?? undefined}>
                    {withMark ? (
                      <td data-label={u('mark')}>
                        {item.label ? (
                          <StatusBadge
                            tone={
                              item.mark === 'void'
                                ? 'danger'
                                : item.mark === 'reapproval_pending'
                                  ? 'warning'
                                  : 'neutral'
                            }
                          >
                            {item.label}
                          </StatusBadge>
                        ) : null}
                      </td>
                    ) : null}
                    {table.columns.map((column, i) => (
                      <td
                        key={column.key}
                        data-label={column.label}
                        className={column.kind === 'text' ? undefined : css.cell}
                        data-numeric={numeric(column.kind) || undefined}
                      >
                        {column.kind === 'text' ? (
                          <span className={css.wrap}>
                            {reportCellText(column.kind, item.row[i] ?? null, locale)}
                          </span>
                        ) : (
                          reportCellText(column.kind, item.row[i] ?? null, locale)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          {rows.length > PAGE_SIZE ? (
            <TablePager
              page={current}
              pages={pages}
              summary={u('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, rows.length),
                total: rows.length,
              })}
              label={u('pages', { name: table.title })}
              onPage={setPage}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Raporlar sayfasındaki (`/reports`) PMS bölümü (plan M7 Task 31; M12 Reports kalıbı): rapor
 * başına bağlantı, grup sütunlarında; History ▸ Reports önizlemesini açar.
 */
export function PmsReportLinks() {
  const { t } = useTranslation();
  const shell = useShell();
  const u = (key: string) => t(`pmsReports.ui.${key}`);
  return (
    <Panel title={u('pmsTitle')}>
      <p className={css.intro}>{u('pmsText')}</p>
      <div className={css.linkGroups}>
        {PMS_REPORT_GROUPS.map((group) => (
          <nav
            key={group}
            className={css.linkGroup}
            aria-label={t(`pmsReports.ui.group`, { group: t(`pmsReports.groups.${group}`) })}
          >
            <h3 className={css.linkTitle}>
              <span className={css.linkIcon} aria-hidden>
                {GROUP_ICONS[group]}
              </span>
              {t(`pmsReports.groups.${group}`)}
            </h3>
            <ul className={css.links}>
              {PMS_REPORTS.filter((def) => def.group === group).map((def) => {
                const path = `/pms/history?tab=reports&report=${def.id}`;
                return (
                  <li key={def.id}>
                    <a
                      href={path}
                      onClick={(event) => {
                        if (shell) followLink(event, path, shell.navigate);
                      }}
                    >
                      {t(`pmsReports.reports.${def.id}.title`)}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        ))}
      </div>
    </Panel>
  );
}
